// ⚡ VTS Signal Engine — scores recent-tick setups per game type.
// A "setup" = several conditions lining up. NEVER a guarantee.

const ema = (arr, k) => {
  const a = 2 / (k + 1);
  return arr.reduce((p, v, i) => (i ? v * a + p * (1 - a) : v), 0);
};

export function riseFallSignal(prices) {
  if (!prices || prices.length < 23) return { wait: true, strength: 0, reasons: [] };
  const moves = [];
  for (let i = 1; i < prices.length; i += 1) {
    if (prices[i] !== prices[i - 1]) moves.push(prices[i] > prices[i - 1] ? 1 : -1);
  }
  if (moves.length < 10) return { wait: true, strength: 0, reasons: [] };
  const up10 = moves.slice(-10).filter((m) => m > 0).length;
  const upAll = Math.round((moves.filter((m) => m > 0).length / moves.length) * 100);
  const sma20 = prices.slice(-20).reduce((a, b) => a + b, 0) / 20;
  const last = prices[prices.length - 1];
  let streak = 1;
  for (let i = moves.length - 2; i >= 0 && moves[i] === moves[moves.length - 1]; i -= 1) streak += 1;
  const streakDir = moves[moves.length - 1];
  const e5 = ema(prices.slice(-20), 5);
  const e12 = ema(prices.slice(-20), 12);
  const e5p = ema(prices.slice(-23, -3), 5);
  const e12p = ema(prices.slice(-23, -3), 12);
  const crossUp = e5 > e12 && e5p <= e12p;
  const crossDown = e5 < e12 && e5p >= e12p;
  // 🧭 trend = price vs 20-tick average AND fast/slow MA agreement
  const trendDir = last > sma20 && e5 > e12 ? 1 : last < sma20 && e5 < e12 ? -1 : 0;

  const score = (dir) => {
    let s = 0;
    const why = [];
    if (dir > 0) {
      if (crossUp) { s += 25; why.push("fresh bullish MA cross (5/12)"); }
      if (up10 >= 6) { s += 20; why.push(`${up10} of last 10 moves up`); }
      if (upAll >= 55) { s += 10; why.push(`${upAll}% of last 50 moves up`); }
      if (last > sma20) { s += 15; why.push("price above 20-tick average"); }
      if (e5 > e12) { s += 15; why.push("fast avg above slow avg"); }
      if (streakDir > 0 && streak >= 3) { s += 15; why.push(`${streak} straight up ticks`); }
    } else {
      if (crossDown) { s += 25; why.push("fresh bearish MA cross (5/12)"); }
      if (up10 <= 4) { s += 20; why.push(`${10 - up10} of last 10 moves down`); }
      if (upAll <= 45) { s += 10; why.push(`${100 - upAll}% of last 50 moves down`); }
      if (last < sma20) { s += 15; why.push("price below 20-tick average"); }
      if (e5 < e12) { s += 15; why.push("fast avg below slow avg"); }
      if (streakDir < 0 && streak >= 3) { s += 15; why.push(`${streak} straight down ticks`); }
    }
    return { s, why };
  };

  const up = score(1);
  const down = score(-1);
  const side = up.s >= down.s ? "RISE" : "FALL";
  const best = side === "RISE" ? up : down;
  const other = side === "RISE" ? down : up;
  const strength = Math.min(100, best.s);
  const sideDir = side === "RISE" ? 1 : -1;
  const withTrend = trendDir === 0 ? null : trendDir === sideDir;
  // trend gate: only trade WITH the trend — against-trend setups are skipped
  const wait = strength < 55 || best.s - other.s < 20 || withTrend === false;
  const reasons = [...best.why];
  if (withTrend === true) reasons.push(side === "RISE" ? "✅ with the UP-trend" : "✅ with the DOWN-trend");
  if (withTrend === false) reasons.push("⛔ against the trend — gated out");
  const last20 = prices.slice(-20);
  const durationSec = strength >= 80 || streak >= 4 ? 30 : 60;
  return {
    side, strength, reasons, wait, risePct: upAll, trend: trendDir,
    support: Math.min(...last20), resistance: Math.max(...last20),
    lastPrice: last, sma20,
    durationSec, durationLabel: durationSec === 30 ? "30s" : "1m",
    durationWhy: durationSec === 30 ? "fast momentum — short run" : "steady trend — 1-minute hold",
  };
}

export function diceSignal(freq, n) {
  if (!freq || !n) return { wait: true, strength: 0, reasons: [] };
  let best = null;
  for (let bar = 2; bar <= 7; bar += 1) {
    const over = (freq.reduce((s, c, d) => (d > bar ? s + c : s), 0) / n) * 100;
    const under = (freq.reduce((s, c, d) => (d < bar ? s + c : s), 0) / n) * 100;
    const cands = [
      { side: "OVER", bar, recent: Math.round(over), theo: (9 - bar) * 10, edge: over - (9 - bar) * 10 },
      { side: "UNDER", bar, recent: Math.round(under), theo: bar * 10, edge: under - bar * 10 },
    ];
    cands.forEach((c) => { if (!best || c.edge > best.edge) best = c; });
  }
  const strength = Math.max(0, Math.min(100, Math.round(best.edge * 5)));
  return {
    ...best, edge: Math.round(best.edge), strength, wait: best.edge < 8,
    reasons: [`${best.side} ${best.bar} landed ${best.recent}% of the last ${n} ticks (typical ${best.theo}%)`],
  };
}

export function luckySignal(freq, n) {
  if (!freq || !n) return { wait: true, strength: 0, reasons: [] };
  let top = 0;
  for (let d = 1; d < 10; d += 1) if (freq[d] > freq[top]) top = d;
  const topPct = Math.round((freq[top] / n) * 100);
  const strength = Math.max(0, Math.min(100, (topPct - 10) * 8));
  return {
    digit: top, topPct, strength, wait: topPct < 16,
    reasons: [`#${top} appeared ${freq[top]}× in the last ${n} ticks (typical ~${Math.round(n / 10)}×)`],
  };
}

// 📏 pip-aware: infer max decimals across the batch so trailing zeros
// (655.30 → digit 0) aren't misread as the previous digit.
const batchDigits = (prices) => {
  let dec = 0;
  prices.forEach((p) => {
    const s = String(p);
    const i = s.indexOf(".");
    const d = i < 0 ? 0 : s.length - i - 1;
    if (d > dec) dec = d;
  });
  return prices.map((p) => parseInt(Number(p).toFixed(dec).slice(-1), 10));
};

export function evenOddSignal(prices) {
  if (!prices || prices.length < 20) return { wait: true, strength: 0, reasons: [] };
  const digits = batchDigits(prices);
  const evens = digits.filter((d) => d % 2 === 0).length;
  const evenPct = Math.round((evens / digits.length) * 100);
  const last10 = digits.slice(-10);
  const even10 = last10.filter((d) => d % 2 === 0).length;
  let streak = 1;
  const lastPar = digits[digits.length - 1] % 2;
  for (let i = digits.length - 2; i >= 0 && digits[i] % 2 === lastPar; i -= 1) streak += 1;

  const score = (even) => {
    let s = 0;
    const why = [];
    const pct = even ? evenPct : 100 - evenPct;
    const c10 = even ? even10 : 10 - even10;
    if (pct >= 58) { s += 30; why.push(`${pct}% of last ${digits.length} digits ${even ? "even" : "odd"}`); }
    if (c10 >= 7) { s += 35; why.push(`${c10} of last 10 digits ${even ? "even" : "odd"}`); }
    if (streak >= 4 && (lastPar === 0) === even) { s += 35; why.push(`${streak} straight ${even ? "even" : "odd"} digits`); }
    return { s, why };
  };
  const e = score(true);
  const o = score(false);
  const side = e.s >= o.s ? "EVEN" : "ODD";
  const best = side === "EVEN" ? e : o;
  const other = side === "EVEN" ? o : e;
  const strength = Math.min(100, best.s);
  return { side, strength, reasons: best.why, wait: strength < 55 || best.s - other.s < 20, evenPct };
}
